"use client";

import { c as _c } from "react/compiler-runtime";
import { type PointerEvent as ReactPointerEvent, useCallback, useLayoutEffect, useRef, useState } from "react";
import type { FloatingChatSide } from "./floating-chat-context";

/** Travel (px) before a press becomes a drag; below it, the press is a click. */
const DRAG_THRESHOLD = 4;

/*
 * How far ahead (ms) the release velocity is projected when picking a corner,
 * so a short flick toward a corner lands there without dragging all the way.
 */
const FLICK_PROJECTION_MS = 150;

/*
 * Velocity is only sampled on movement, so a drag that stops before release
 * still holds its last speed. It fades to nothing over this long (ms) of
 * stillness: a pause and release lands where the launcher is, not where it
 * was heading.
 */
const VELOCITY_IDLE_MS = 100;

/* Critically damped feel: fast out of the hand, settling without overshoot. */
const SNAP_TRANSITION = "transform 300ms cubic-bezier(0.2, 0, 0, 1)";
interface Gesture {
  dragging: boolean;
  dx: number;
  dy: number;
  lastT: number;
  lastX: number;
  lastY: number;
  pointerId: number;
  rect: DOMRect;
  startX: number;
  startY: number;
  vx: number;
  vy: number;
}
function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}
function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** The corner whose quadrant holds the point. */
export function cornerAt(x: number, y: number): FloatingChatSide {
  const vertical = y < window.innerHeight / 2 ? "top" : "bottom";
  const horizontal = x < window.innerWidth / 2 ? "left" : "right";
  return `${vertical}-${horizontal}`;
}

/** Ends a snap still in flight, per launcher element. */
const pendingSettles = new WeakMap<HTMLElement, () => void>();

/** Stops `el`'s snap where its styles leave it: at its corner anchor. */
function cancelSettle(el: HTMLElement) {
  pendingSettles.get(el)?.();
}
function settle(el: HTMLElement, from: {
  x: number;
  y: number;
} | null) {
  cancelSettle(el);
  const reduced = prefersReducedMotion();
  if (from && !reduced) {
    el.style.transition = "none";
    el.style.transform = `translate3d(${from.x}px, ${from.y}px, 0)`;
    // Commit the start frame before transitioning away from it.
    el.getBoundingClientRect();
  }
  if (reduced) {
    el.style.transition = "";
    el.style.transform = "";
    return;
  }
  // The launcher's own color and the bezel tile's shadow transition too, and
  // the tile's events bubble here: only the launcher's transform ends a snap.
  const done = (event?: TransitionEvent) => {
    if (event && (event.target !== el || event.propertyName !== "transform")) {
      return;
    }
    el.style.transition = "";
    el.removeEventListener("transitionend", done);
    el.removeEventListener("transitioncancel", done);
    pendingSettles.delete(el);
  };
  el.addEventListener("transitionend", done);
  el.addEventListener("transitioncancel", done);
  pendingSettles.set(el, () => done());
  el.style.transition = SNAP_TRANSITION;
  el.style.transform = "";
}

/**
 * Pointer-driven repositioning for a corner-anchored element (the launcher,
 * or the open panel by its header): past a small threshold a press becomes a
 * drag that moves `ref`'s element with the pointer (kept inside the
 * viewport), and on release it snaps to the nearest corner — FLIP-animated
 * from where it was let go to its new anchor, or instantly under reduced
 * motion. A drag never also counts as a click.
 *
 * The element is anchored by CSS to a corner, so a viewport resize keeps it
 * in bounds on its own; only the in-flight offset is clamped here.
 */
export function useCornerDrag(t0) {
  const $ = _c(23);
  const {
    enabled,
    onSideChange,
    side,
    startsDrag
  } = t0;
  const ref = useRef(null);
  const gesture = useRef(null);
  const frame = useRef(0);
  const releasedAt = useRef(null);
  const swallowClick = useRef(false);
  const [dragging, setDragging] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const el = ref.current;
      const from = releasedAt.current;
      if (!(el && from)) {
        return;
      }
      releasedAt.current = null;
      el.style.transition = "none";
      el.style.transform = "";
      const rect = el.getBoundingClientRect();
      settle(el, {
        x: from.x - rect.left,
        y: from.y - rect.top
      });
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== side) {
    t2 = [side];
    $[1] = side;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useLayoutEffect(t1, t2);
  let t3;
  if ($[3] !== enabled || $[4] !== startsDrag) {
    t3 = event => {
      swallowClick.current = false;
      if (!enabled || event.button !== 0 || !event.isPrimary) {
        return;
      }
      if (startsDrag && !(event.target instanceof Element && startsDrag(event.target))) {
        return;
      }
      const el_0 = event.currentTarget;
      cancelSettle(el_0);
      el_0.setPointerCapture(event.pointerId);
      gesture.current = {
        dragging: false,
        dx: 0,
        dy: 0,
        lastT: event.timeStamp,
        lastX: event.clientX,
        lastY: event.clientY,
        pointerId: event.pointerId,
        rect: el_0.getBoundingClientRect(),
        startX: event.clientX,
        startY: event.clientY,
        vx: 0,
        vy: 0
      };
    };
    $[3] = enabled;
    $[4] = startsDrag;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const onPointerDown = t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = event_0 => {
      const g = gesture.current;
      if (!g || g.pointerId !== event_0.pointerId) {
        return;
      }
      const dt = Math.max(event_0.timeStamp - g.lastT, 1);
      g.vx = (event_0.clientX - g.lastX) / dt;
      g.vy = (event_0.clientY - g.lastY) / dt;
      g.lastT = event_0.timeStamp;
      g.lastX = event_0.clientX;
      g.lastY = event_0.clientY;
      const rawX = event_0.clientX - g.startX;
      const rawY = event_0.clientY - g.startY;
      if (!g.dragging) {
        if (Math.hypot(rawX, rawY) < DRAG_THRESHOLD) {
          return;
        }
        g.dragging = true;
        setDragging(true);
      }
      g.dx = clamp(rawX, -g.rect.left, window.innerWidth - g.rect.right);
      g.dy = clamp(rawY, -g.rect.top, window.innerHeight - g.rect.bottom);
      const el_1 = event_0.currentTarget;
      cancelAnimationFrame(frame.current);
      frame.current = requestAnimationFrame(() => {
        el_1.style.transition = "none";
        el_1.style.transform = `translate3d(${g.dx}px, ${g.dy}px, 0)`;
      });
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const onPointerMove = t4;
  let t5;
  if ($[7] !== onSideChange || $[8] !== side) {
    t5 = (event_1, cancelled) => {
      const g_0 = gesture.current;
      if (!g_0 || g_0.pointerId !== event_1.pointerId) {
        return;
      }
      gesture.current = null;
      cancelAnimationFrame(frame.current);
      if (!g_0.dragging) {
        return;
      }
      swallowClick.current = true;
      setDragging(false);
      const el_2 = event_1.currentTarget;
      const left = g_0.rect.left + g_0.dx;
      const top = g_0.rect.top + g_0.dy;
      const idle = event_1.timeStamp - g_0.lastT;
      const carry = FLICK_PROJECTION_MS * Math.max(0, 1 - idle / VELOCITY_IDLE_MS);
      const next = cancelled ? side : cornerAt(left + g_0.rect.width / 2 + g_0.vx * carry, top + g_0.rect.height / 2 + g_0.vy * carry);
      if (next === side) {
        settle(el_2, null);
        return;
      }
      releasedAt.current = {
        x: left,
        y: top
      };
      onSideChange(next);
    };
    $[7] = onSideChange;
    $[8] = side;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const finish = t5;
  let t6;
  if ($[10] !== finish) {
    t6 = event_2 => finish(event_2, false);
    $[10] = finish;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const onPointerUp = t6;
  let t7;
  if ($[12] !== finish) {
    t7 = event_3 => finish(event_3, true);
    $[12] = finish;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const onPointerCancel = t7;
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      const swallow = swallowClick.current;
      swallowClick.current = false;
      return swallow;
    };
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const consumeDragClick = t8;
  let t9;
  if ($[15] !== onPointerCancel || $[16] !== onPointerDown || $[17] !== onPointerUp) {
    t9 = {
      onPointerCancel,
      onPointerDown,
      onPointerMove,
      onPointerUp
    };
    $[15] = onPointerCancel;
    $[16] = onPointerDown;
    $[17] = onPointerUp;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== dragging || $[20] !== enabled || $[21] !== t9) {
    t10 = {
      active: enabled,
      consumeDragClick,
      dragging,
      handlers: t9,
      ref
    };
    $[19] = dragging;
    $[20] = enabled;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
}